"use client";

import { useMemo, useCallback } from "react";
import type { MultiSelectOptionT } from "@/app/components/ui/multi-select";

type UseMultiSelectLogicPropsT = {
  options: MultiSelectOptionT[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  maxSelections?: number;
  disabled?: boolean;
};

/**
 * Custom hook for multi-select business logic
 *
 * Handles all selection operations including:
 * - Adding/removing individual options with validation
 * - Clearing all selections
 * - Enforcing maxSelections limits
 * - Processing selected options for display
 *
 * @param props - Hook configuration
 * @returns Selection handlers and processed data
 *
 * @example
 * ```typescript
 * const {
 *   selectedOptions,
 *   handleSelect,
 *   handleRemove,
 *   handleClearAll
 * } = useMultiSelectLogic({
 *   options,
 *   selectedValues,
 *   onSelectionChange,
 *   maxSelections: 3,
 *   disabled: false
 * });
 * ```
 */
export function useMultiSelectLogic({
  options,
  selectedValues,
  onSelectionChange,
  maxSelections,
  disabled = false,
}: UseMultiSelectLogicPropsT) {
  // Get selected options for display in selection order - memoized for performance
  const selectedOptions = useMemo(() => {
    // Preserve the order in which items were selected (selectedValues order)
    // instead of the order they appear in the options array
    return selectedValues
      .map((value) => options.find((option) => option.value === value))
      .filter((option): option is MultiSelectOptionT => option !== undefined);
  }, [options, selectedValues]);

  // Handle selecting/deselecting options
  const handleSelect = useCallback(
    (value: string) => {
      if (disabled) return;

      const option = options.find((opt) => opt.value === value);
      if (option?.disabled) return;

      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];

      // Check maxSelections limit when adding new items
      if (
        maxSelections &&
        !selectedValues.includes(value) &&
        newSelectedValues.length > maxSelections
      ) {
        return; // Don't add if it exceeds the limit
      }

      onSelectionChange(newSelectedValues);
    },
    [disabled, options, selectedValues, maxSelections, onSelectionChange]
  );

  // Handle removing individual option
  const handleRemove = useCallback(
    (valueToRemove: string) => {
      if (disabled) return;
      onSelectionChange(
        selectedValues.filter((value) => value !== valueToRemove)
      );
    },
    [disabled, selectedValues, onSelectionChange]
  );

  // Handle clearing all selections
  const handleClearAll = useCallback(() => {
    if (disabled) return;
    onSelectionChange([]);
  }, [disabled, onSelectionChange]);

  return {
    selectedOptions,
    handleSelect,
    handleRemove,
    handleClearAll,
  };
}
